import { todayISO, type Transaction } from '@keel/core';
import { withLockConfirm } from '@/lib/locked';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, FileText, Lock, Paperclip, Plus, Split as SplitIcon, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { uploadFile } from '@/api/client';
import { errorMessage, useTRPC, useTRPCClient } from '@/api/query';
import {
  Badge, Button, Checkbox, Combobox, DateInput, Field, IconButton, Input, Kbd, MoneyInput, NumberInput, Select, SheetContent, Sheet,
  Tabs, TabsList, TabsTrigger, toast, type ComboboxOption,
} from '@/components/ui';
import { isMac } from '@/lib/keyboard';
import { CURRENCY_OPTIONS } from '@/lib/countries';
import { accountOptions, categoryOptions, contactHint, useCreateCategory, useLedger, useOtherEntityTargets, useRefresh } from './data';
import { MultiSelect } from './MultiSelect';
import { newSplitDraft, SplitEditor } from './SplitEditor';
import { splitRemainder, type SplitDraft } from './splits';
import { useTxActions } from './tx-actions';

type Mode = 'out' | 'in' | 'transfer';
interface Draft {
  readonly mode: Mode;
  readonly date: string;
  readonly accountId: string;
  readonly toAccountId: string;
  readonly payeeId: string | null;
  readonly payeeName: string;
  /** Typed name to create as a new payee on save. */
  readonly newPayee: boolean;
  /** Positive magnitude; the sign comes from `mode`. */
  readonly amountCents: number | null;
  readonly toAmountCents: number | null;
  readonly categoryId: string | null;
  readonly split: boolean;
  readonly splits: SplitDraft[];
  readonly memo: string;
  readonly reference: string;
  readonly tagIds: string[];
  readonly status: Transaction['status'];
  readonly fx: boolean;
  readonly originalCurrency: string;
  readonly originalAmountCents: number | null;
  readonly fxRate: number | null;
}

export interface EditorDefaults {
  readonly accountId?: string;
  readonly date?: string;
}

const blank = (d: EditorDefaults, fallbackAccount: string, baseCurrency: string): Draft => ({
  mode: 'out', date: d.date ?? todayISO(), accountId: d.accountId ?? fallbackAccount, toAccountId: '', payeeId: null, payeeName: '',
  newPayee: false, amountCents: null, toAmountCents: null, categoryId: null, split: false, splits: [], memo: '', reference: '', tagIds: [],
  status: 'cleared', fx: false, originalCurrency: baseCurrency, originalAmountCents: null, fxRate: null,
});

const fromTx = (t: Transaction, payeeName: string, baseCurrency: string): Draft => ({
  mode: t.transferId ? 'transfer' : t.amountCents >= 0 ? 'in' : 'out',
  date: t.date,
  accountId: t.transferId && t.amountCents > 0 ? t.transferAccountId ?? t.accountId : t.accountId,
  toAccountId: t.transferId ? (t.amountCents > 0 ? t.accountId : t.transferAccountId ?? '') : '',
  payeeId: t.payeeId, payeeName, newPayee: false, amountCents: Math.abs(t.amountCents), toAmountCents: null,
  categoryId: t.splits[0]?.categoryId ?? null, split: t.splits.length > 1,
  splits: t.splits.map((s) => ({ key: s.id, categoryId: s.categoryId, amountCents: s.amountCents, memo: s.memo, tagIds: s.tagIds, percent: null })),
  memo: t.memo, reference: t.reference, tagIds: [...new Set(t.splits.flatMap((s) => s.tagIds))], status: t.status,
  fx: t.originalCurrency !== null, originalCurrency: t.originalCurrency ?? baseCurrency, originalAmountCents: t.originalAmountCents, fxRate: t.fxRate,
});

export interface TransactionEditorProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Edit this transaction; null/undefined creates a new one. */
  readonly transaction?: Transaction | null;
  readonly defaults?: EditorDefaults;
  readonly onSaved?: (t: Transaction) => void;
}

/**
 * Full transaction editor (side sheet): money out / in / transfer, payee with suggestions, category or splits,
 * tags, status, attachments and FX. `Enter` saves, `⌘Enter` saves and starts another.
 */
export const TransactionEditor = ({ open, onOpenChange, transaction, defaults = {}, onSaved }: TransactionEditorProps) => {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const qc = useQueryClient();
  const refresh = useRefresh();
  const actions = useTxActions();
  const { entityId, openAccounts, accounts, accountById, categories, payees, payeeById, tags, baseCurrency, canEdit } = useLedger();
  const fallbackAccount = openAccounts[0]?.id ?? '';
  const [draft, setDraft] = useState<Draft>(() => blank(defaults, fallbackAccount, baseCurrency));
  const [errors, setErrors] = useState<Partial<Record<'amount' | 'account' | 'toAccount' | 'splits', string>>>({});
  const [saving, setSaving] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [queued, setQueued] = useState<File[]>([]);
  const [remember, setRemember] = useState(true);
  const fileInput = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  // Reset whenever the sheet opens for a different record.
  useEffect(() => {
    if (!open) return;
    const name = transaction ? (transaction.payeeId ? payeeById.get(transaction.payeeId)?.name : undefined) ?? transaction.payeeRaw : '';
    setDraft(transaction ? fromTx(transaction, name, baseCurrency) : blank(defaults, fallbackAccount, baseCurrency));
    setAttachments(transaction?.attachmentIds ?? []);
    setQueued([]);
    setErrors({});
    setSuggested(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction?.id]);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const locked = transaction?.status === 'reconciled';
  const isTransfer = draft.mode === 'transfer';
  // transfers can go to an account in another entity you can edit (business ↔ personal: drawings / owner contributions)
  const otherTargets = useOtherEntityTargets(entityId, isTransfer);
  const account = accountById.get(draft.accountId);
  const toAccount = accountById.get(draft.toAccountId) ?? otherTargets.byId.get(draft.toAccountId);
  const currency = account?.currency ?? baseCurrency;
  const sign = draft.mode === 'out' ? -1 : 1;
  const signed = draft.amountCents === null ? null : sign * draft.amountCents;
  const crossCurrency = isTransfer && toAccount && account && toAccount.currency !== account.currency;
  const showFx = !isTransfer && (draft.fx || currency !== baseCurrency);
  const editingTransfer = Boolean(transaction?.transferId);
  /** An existing payment being turned into a transfer to one of your accounts (a loan repayment, a card payment). */
  const converting = Boolean(transaction && !editingTransfer && isTransfer);
  const outgoing = (transaction?.amountCents ?? -1) < 0;

  const accountOpts = useMemo(() => accountOptions(accounts.filter((a) => !a.isClosed || a.id === draft.accountId || a.id === draft.toAccountId)), [accounts, draft.accountId, draft.toAccountId]);
  const catOpts = useMemo(() => categoryOptions(categories), [categories]);
  const createCategory = useCreateCategory(draft.mode === 'in' ? 'income' : 'expense');
  const loadPayees = useMemo(
    () => async (q: string): Promise<ComboboxOption[]> => {
      const needle = q.trim().toLowerCase();
      const hits = payees
        .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.aliases.some((a) => a.toLowerCase().includes(needle)))
        .slice(0, 50)
        .map((p) => ({ value: p.id, label: p.name, hint: contactHint(p) }));
      const exact = payees.some((p) => p.name.toLowerCase() === needle);
      return needle && !exact ? [...hits, { value: `new:${q.trim()}`, label: `Add “${q.trim()}” as a new payee` }] : hits;
    },
    [payees],
  );

  const choosePayee = async (value: string, label: string) => {
    if (value.startsWith('new:')) {
      set({ payeeId: null, payeeName: value.slice(4), newPayee: true });
      return;
    }
    const p = payeeById.get(value);
    set({ payeeId: value, payeeName: p?.name ?? label, newPayee: false });
    if (draft.categoryId || draft.split || !entityId) return;
    if (p?.defaultCategoryId) {
      set({ categoryId: p.defaultCategoryId });
      setSuggested(true);
      return;
    }
    try {
      const s = await qc.fetchQuery(trpc.payees.suggest.queryOptions({ entityId, raw: p?.name ?? label }));
      if (s.categoryId) {
        set({ categoryId: s.categoryId });
        setSuggested(true);
      }
    } catch {
      // A suggestion is a nicety; the user can still pick a category.
    }
  };

  const setMode = (mode: Mode) => {
    if (mode === draft.mode) return;
    const flip = (mode === 'out') !== (draft.mode === 'out') && mode !== 'transfer' && draft.mode !== 'transfer';
    set({ mode, splits: flip ? draft.splits.map((s) => ({ ...s, amountCents: -s.amountCents })) : draft.splits });
  };

  const toggleSplit = (on: boolean) => {
    if (on && !draft.splits.length) {
      const total = signed ?? 0;
      set({ split: true, splits: [newSplitDraft(total, draft.categoryId), newSplitDraft(0)] });
    } else set({ split: on });
  };

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!draft.amountCents) e.amount = 'Enter an amount.';
    if (!draft.accountId) e.account = 'Choose an account.';
    if (isTransfer && !draft.toAccountId) e.toAccount = 'Choose the account the money goes to.';
    if (isTransfer && draft.toAccountId === draft.accountId) e.toAccount = 'Pick two different accounts.';
    if (!isTransfer && draft.split && signed !== null && splitRemainder(signed, draft.splits) !== 0) e.splits = 'Splits need to add up to the amount.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (andNew: boolean) => {
    if (!entityId || !validate() || signed === null) return;
    setSaving(true);
    try {
      let saved: Transaction;
      let learned: Parameters<typeof actions.announceLearned>[1];
      if (isTransfer && !transaction) {
        const legs = await client.transactions.createTransfer.mutate({
          entityId, fromAccountId: draft.accountId, toAccountId: draft.toAccountId, date: draft.date, amountCents: draft.amountCents ?? 0,
          ...(crossCurrency && draft.toAmountCents ? { toAmountCents: draft.toAmountCents } : {}), ...(draft.memo ? { memo: draft.memo } : {}),
        });
        saved = legs[0];
      } else if (transaction && converting) {
        const r = await client.transactions.payToAccount.mutate({ entityId, ids: [transaction.id], toAccountId: draft.toAccountId, remember });
        saved = r.legs.find((l) => l.id === transaction.id) ?? r.legs[0]!;
        if (r.rule) toast.success(`Future payments like this will go to ${toAccount?.name ?? 'that account'} too`);
      } else if (transaction && editingTransfer) {
        saved = await withLockConfirm((flag) => client.transactions.update.mutate({
          id: transaction.id, date: draft.date, amountCents: Math.sign(transaction.amountCents || -1) * (draft.amountCents ?? 0),
          memo: draft.memo, reference: draft.reference, ...(locked ? {} : { status: draft.status }), ...flag,
        }));
      } else {
        let payeeId = draft.payeeId;
        if (draft.newPayee && draft.payeeName.trim()) {
          payeeId = (await client.payees.create.mutate({ entityId, name: draft.payeeName.trim(), defaultCategoryId: draft.split ? null : draft.categoryId })).id;
        }
        const splits = draft.split
          ? draft.splits.map((s) => ({ categoryId: s.categoryId, amountCents: s.amountCents, memo: s.memo, tagIds: draft.tagIds }))
          : [{ categoryId: draft.categoryId, amountCents: signed, memo: '', tagIds: draft.tagIds }];
        const fx = showFx && draft.originalAmountCents
          ? { originalAmountCents: sign * Math.abs(draft.originalAmountCents), originalCurrency: draft.originalCurrency, fxRate: draft.fxRate }
          : { originalAmountCents: null, originalCurrency: null, fxRate: null };
        const body = {
          date: draft.date, accountId: draft.accountId, amountCents: signed, payeeId, payeeRaw: draft.payeeName.trim(), memo: draft.memo,
          reference: draft.reference, splits, isReviewed: true, ...fx, ...(locked ? {} : { status: draft.status }),
        };
        const { date, accountId, amountCents, ...rest } = body;
        if (transaction) {
          const updated = await withLockConfirm((flag) => client.transactions.update.mutate({ id: transaction.id, ...(locked ? {} : { date, accountId, amountCents }), ...rest, ...flag }));
          learned = updated.learned;
          saved = updated;
        } else saved = await client.transactions.create.mutate({ entityId, ...body, status: draft.status === 'reconciled' ? 'cleared' : draft.status });
      }
      for (const f of queued) {
        const up = await uploadFile(f);
        await client.transactions.attach.mutate({ transactionId: saved.id, fileToken: up.fileToken });
      }
      await refresh();
      toast.success(transaction ? 'Transaction saved' : 'Transaction added');
      if (transaction) actions.announceLearned(transaction.entityId, learned);
      onSaved?.(saved);
      if (andNew) {
        setDraft({ ...blank({ accountId: draft.accountId, date: draft.date }, fallbackAccount, baseCurrency), mode: draft.mode === 'transfer' ? 'out' : draft.mode });
        setQueued([]);
        setAttachments([]);
        setSuggested(false);
        if (transaction) onOpenChange(false);
        requestAnimationFrame(() => amountRef.current?.focus());
      } else onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const attach = async (files: FileList | null) => {
    if (!files?.length) return;
    if (!transaction) {
      setQueued((q) => [...q, ...Array.from(files)]);
      return;
    }
    try {
      for (const f of Array.from(files)) {
        const up = await uploadFile(f);
        const a = await client.transactions.attach.mutate({ transactionId: transaction.id, fileToken: up.fileToken });
        setAttachments((x) => [...x, a.id]);
      }
      toast.success('Receipt attached');
      void refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const detach = async (attachmentId: string) => {
    if (!transaction) return;
    try {
      await client.transactions.detach.mutate({ transactionId: transaction.id, attachmentId });
      setAttachments((x) => x.filter((id) => id !== attachmentId));
      void refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void save(true);
    }
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void save(false);
  };

  const title = transaction ? (editingTransfer ? 'Edit transfer' : 'Edit transaction') : 'New transaction';
  const readOnly = !canEdit;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        title={title}
        description={locked ? undefined : `Enter saves. ${isMac() ? '⌘' : 'Ctrl'} Enter saves and starts another.`}
        className="max-w-lg"
        footer={
          readOnly ? null : (
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {transaction ? (
                <Button
                  variant="ghost"
                  className="text-negative"
                  icon={<Trash2 aria-hidden />}
                  disabled={locked}
                  onClick={() => {
                    onOpenChange(false);
                    void actions.remove([transaction]);
                  }}
                >
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button variant="outline" disabled={saving} onClick={() => void save(true)}>
                  Save and new <Kbd className="hidden sm:inline-flex">{isMac() ? '⌘' : 'Ctrl'}↵</Kbd>
                </Button>
                <Button type="submit" form="tx-editor" loading={saving}>
                  Save
                </Button>
              </div>
            </div>
          )
        }
      >
        <form id="tx-editor" onSubmit={onSubmit} onKeyDown={onKeyDown} className="flex flex-col gap-4" aria-label={title}>
          {locked ? (
            <p className="flex items-center gap-2 rounded-md bg-surface-sunken px-3 py-2 text-sm text-text-muted">
              <Lock className="size-4" aria-hidden /> Reconciled. Date, account and amount are locked; undo the reconciliation to change them.
            </p>
          ) : null}
          <Tabs value={draft.mode} onValueChange={(v) => setMode(v as Mode)}>
            <TabsList aria-label="Kind of transaction" className="w-full">
              <TabsTrigger value="out" className="flex-1" disabled={editingTransfer}>
                <ArrowUpRight className="size-4" aria-hidden /> Money out
              </TabsTrigger>
              <TabsTrigger value="in" className="flex-1" disabled={editingTransfer}>
                <ArrowDownLeft className="size-4" aria-hidden /> Money in
              </TabsTrigger>
              <TabsTrigger value="transfer" className="flex-1" disabled={Boolean(transaction) && !editingTransfer && (transaction!.splits.length > 1 || locked)}>
                <ArrowLeftRight className="size-4" aria-hidden /> Transfer
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Amount" error={errors.amount}>
              <MoneyInput
                ref={amountRef}
                autoFocus
                value={draft.amountCents}
                allowNegative={false}
                currency={currency}
                disabled={locked || readOnly || converting}
                onChange={(c) => set({ amountCents: c })}
              />
            </Field>
            <Field label="Date">
              <DateInput value={draft.date} required disabled={locked || readOnly || converting} onChange={(e) => set({ date: e.target.value })} />
            </Field>
          </div>

          <Field label={converting ? 'This account' : isTransfer ? 'From account' : 'Account'} error={errors.account}>
            <Combobox
              value={draft.accountId || null}
              options={accountOpts}
              placeholder="Choose an account"
              disabled={locked || readOnly || editingTransfer || converting}
              onChange={(v) => set({ accountId: v })}
            />
          </Field>

          {isTransfer ? (
            <>
              <Field
                label={converting ? (outgoing ? 'Paid to' : 'Came from') : 'To account'} error={errors.toAccount}
                hint={converting ? (outgoing ? 'Your loan, card or other account. Its balance goes down by this payment.' : 'The account the money came from.') : undefined}
              >
                <Combobox
                  value={draft.toAccountId || null}
                  options={[
                    ...accountOpts.filter((o) => o.value !== draft.accountId),
                    ...otherTargets.options,
                  ]}
                  placeholder="Choose an account"
                  disabled={readOnly || editingTransfer}
                  onChange={(v) => set({ toAccountId: v })}
                />
              </Field>
              {converting ? (
                <Checkbox checked={remember} onCheckedChange={(c) => setRemember(c === true)}
                  label={`Do the same for future payments to ${transaction?.payeeRaw || 'this payee'}`} description="Adds a rule, so new ones are linked as they're imported." />
              ) : null}
              {crossCurrency && !editingTransfer ? (
                <Field label={`Amount received in ${toAccount.currency}`} hint="The difference is recorded as a realised FX gain or loss." optional>
                  <MoneyInput value={draft.toAmountCents} allowNegative={false} currency={toAccount.currency} onChange={(c) => set({ toAmountCents: c })} />
                </Field>
              ) : null}
            </>
          ) : (
            <>
              <Field label="Payee" optional hint={draft.newPayee ? 'A new payee will be added when you save.' : undefined}>
                <Combobox
                  value={draft.payeeId ?? (draft.newPayee ? `new:${draft.payeeName}` : null)}
                  loadOptions={loadPayees}
                  placeholder={draft.payeeName || 'Who was it with?'}
                  searchPlaceholder="Search or add a payee…"
                  disabled={readOnly}
                  onChange={(v, o) => void choosePayee(v, o.label)}
                />
              </Field>
              {draft.split ? (
                <div>
                  <SplitEditor totalCents={signed ?? 0} splits={draft.splits} onChange={(splits) => set({ splits })} categories={categories} currency={currency} onCreateCategory={createCategory} />
                  {errors.splits ? <p className="mt-1 text-xs font-medium text-negative" role="alert">{errors.splits}</p> : null}
                </div>
              ) : (
                <Field label="Category" hint={suggested ? 'Suggested from this payee. Change it if it’s wrong.' : undefined}>
                  <Combobox value={draft.categoryId} options={catOpts} placeholder="Uncategorised" disabled={readOnly} onCreate={createCategory} onChange={(v) => { set({ categoryId: v }); setSuggested(false); }} />
                </Field>
              )}
              {!readOnly ? (
                <Button variant="ghost" size="sm" className="self-start" icon={<SplitIcon aria-hidden />} onClick={() => toggleSplit(!draft.split)} aria-pressed={draft.split}>
                  {draft.split ? 'Use one category' : 'Split across categories'}
                </Button>
              ) : null}
            </>
          )}

          <Field label="Memo" optional>
            <Input value={draft.memo} maxLength={500} disabled={readOnly} onChange={(e) => set({ memo: e.target.value })} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Reference" optional hint="Cheque or receipt number">
              <Input value={draft.reference} maxLength={100} disabled={readOnly} onChange={(e) => set({ reference: e.target.value })} />
            </Field>
            <Field label="Status">
              <Select
                value={draft.status}
                disabled={locked || readOnly}
                onValueChange={(v) => set({ status: v as Draft['status'] })}
                options={[
                  { value: 'cleared', label: 'Cleared' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'void', label: 'Void' },
                  ...(locked ? [{ value: 'reconciled' as const, label: 'Reconciled' }] : []),
                ]}
              />
            </Field>
          </div>

          {!isTransfer ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-text">Tags</span>
              <MultiSelect
                label={tags.length ? 'Add tags' : 'No tags yet'}
                options={tags.map((t) => ({ value: t.id, label: `#${t.name}` }))}
                value={draft.tagIds}
                onChange={(tagIds) => set({ tagIds })}
                className="self-start"
              />
            </div>
          ) : null}

          {!isTransfer ? (
            <div className="flex flex-col gap-3 rounded-md border border-border p-3">
              <Checkbox
                label="Paid in another currency"
                description={currency !== baseCurrency ? `This account is in ${currency}; your books are in ${baseCurrency}.` : 'Record the original amount and rate.'}
                checked={showFx}
                disabled={readOnly || currency !== baseCurrency}
                onCheckedChange={(v) => set({ fx: v === true })}
              />
              {showFx ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Currency">
                    <Select value={draft.originalCurrency} options={CURRENCY_OPTIONS} onValueChange={(v) => set({ originalCurrency: v })} />
                  </Field>
                  <Field label="Original amount">
                    <MoneyInput
                      value={draft.originalAmountCents === null ? null : Math.abs(draft.originalAmountCents)}
                      allowNegative={false}
                      currency={draft.originalCurrency}
                      onChange={(c) => set({
                        originalAmountCents: c,
                        fxRate: c && draft.amountCents ? Math.round((draft.amountCents / c) * 1e6) / 1e6 : draft.fxRate,
                      })}
                    />
                  </Field>
                  <Field label="Rate">
                    <NumberInput value={draft.fxRate} step="0.000001" min={0} onChange={(r) => set({ fxRate: r })} />
                  </Field>
                </div>
              ) : null}
            </div>
          ) : null}

          {!isTransfer ? (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text">Receipts</span>
              {attachments.length || queued.length ? (
                <ul className="flex flex-col gap-1">
                  {attachments.map((id, i) => (
                    <li key={id} className="flex items-center gap-2 text-sm">
                      <FileText className="size-4 text-text-muted" aria-hidden />
                      <a href={`/api/attachments/${id}`} target="_blank" rel="noreferrer" className="flex-1 text-primary underline-offset-4 hover:underline">
                        Attachment {i + 1}
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                      {!readOnly ? <IconButton aria-label={`Remove attachment ${i + 1}`} icon={<X aria-hidden />} onClick={() => void detach(id)} /> : null}
                    </li>
                  ))}
                  {queued.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm">
                      <Paperclip className="size-4 text-text-muted" aria-hidden />
                      <span className="flex-1 truncate">{f.name}</span>
                      <Badge>Uploads on save</Badge>
                      <IconButton aria-label={`Remove ${f.name}`} icon={<X aria-hidden />} onClick={() => setQueued((q) => q.filter((_, j) => j !== i))} />
                    </li>
                  ))}
                </ul>
              ) : null}
              {!readOnly ? (
                <>
                  <Button variant="outline" size="sm" className="self-start" icon={<Plus aria-hidden />} onClick={() => fileInput.current?.click()}>
                    Attach a receipt
                  </Button>
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    className="sr-only"
                    tabIndex={-1}
                    aria-label="Choose receipt files"
                    onChange={(e) => {
                      void attach(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </form>
      </SheetContent>
    </Sheet>
  );
};
